import { useMemo } from 'react';
import { useGetAnalysis } from '../api/generated/insights';
import type {
  AIProviderEnum,
  AnalysisIdOrLatest,
  BrandId,
  Scan,
} from '../api/generated/insights.schemas';
import { useBrandAnalysisData } from '../contexts';

interface UseScanResult {
  data: Scan | null;
  isLoading: boolean;
  isError: boolean;
  isRefetching?: boolean;
}

/**
 * useScan - convenience hook to select a Scan from an Analysis by aiProvider
 *
 * @param brandId - (optional) the brand id
 * @param analysisId - (optional) the analysis id or 'latest'
 * @param model - (optional) AIProviderEnum to match against scan.aiProvider
 */
const useScan = (
  brandId?: BrandId,
  analysisId?: AnalysisIdOrLatest,
  model?: AIProviderEnum,
): UseScanResult => {
  const {
    brandId: brandIdFallback,
    analysisId: analysisIdFallback,
    model: modelFallback,
  } = useBrandAnalysisData();

  const bId = brandId || brandIdFallback;
  const aId = analysisId || analysisIdFallback;
  const m = model || modelFallback;

  const { data, ...rest } = useGetAnalysis(bId, aId, {
    query: {
      enabled: !!bId && !!aId,
      select: ({ data }) => data?.analysis,
    },
  });

  const scan = useMemo(() => {
    if (!data) return null;
    if (!m) return null;
    const scans = data.scans || [];
    return scans.find((s) => String(s.aiProvider) === String(m)) || null;
  }, [data, m]);

  return { data: scan, ...rest };
};

export default useScan;
